import React, { useState, useEffect } from 'react'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import styled from 'styled-components'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import ProgramTable from './Components/ProgramTable'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { getSuperPrograms } from '../Shared/helperFunctions'
import NoPrograms from './Components/NoPrograms'
import SummaryDrawerView from '../Summary/SummaryDrawerView'
import { launchProgramsActions } from '../Launch/launchProgramActions'
import { programEditingActions } from '../Editing/ProgramEditingActions'
import { useDispatch } from 'react-redux'
import { programSuccessActions } from '../Success/ProgramSuccessActions'
import { grhActions } from 'redux/grh/grhSlice'
import { micromobilityActions } from 'redux/micromobility/micromobilitySlice'
import { flexActions } from 'redux/flex/flexSlice'
import { tagsActions } from 'redux/tags/tagsSlice'

const TopButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2rem;
`

const OverviewView = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const allFlexPrograms = useSelector(
    (state: AppState) => state.flex.flexPrograms
  )
  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const allMicromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )
  const { allSegments } = useSelector((state: AppState) => state.employer)
  const availableGrhMerchant = useSelector(
    (state: AppState) => state.grh.availableMerchants
  )
  const availableMicromobilityMerchant = useSelector(
    (state: AppState) => state.micromobility.availableMerchants
  )
  const availableFlexMerchant = useSelector(
    (state: AppState) => state.flex.availableMerchants
  )
  const { incompleteLaunch, draftState } = useSelector(
    (state: AppState) => state.launchPrograms
  )
  const allTags = useSelector((state: AppState) => state.tags.allTags)

  const [isSummaryDrawerOpen, setIsSummaryDrawerOpen] = useState(false)
  const [currentSuperProgram, setCurrentSuperProgram] = useState({})
  const [superPrograms, setSuperPrograms] = useState([])

  // Fetching any draft state
  useEffect(() => {
    dispatch(launchProgramsActions.fetchDraftProgramState())
    dispatch(tagsActions.getTags())
  }, [dispatch])

  useEffect(() => {
    // Getting the merchants for the super programs
    // for the all employees segment
    const allEmployeesSegmentId =
      allSegments.find((segment) => segment.type === 'ALL')?.id || ''
    dispatch(
      grhActions.getGrhRecommendations({ segmentId: allEmployeesSegmentId })
    )
    dispatch(
      micromobilityActions.getMicromobilityRecommendations({
        segmentId: allEmployeesSegmentId,
      })
    )
    dispatch(
      flexActions.getFlexRecommendations({ segmentId: allEmployeesSegmentId })
    )
  }, [])

  useEffect(() => {
    const superProgramsHere = getSuperPrograms(
      allGrhPrograms,
      allQtfPrograms,
      allMicromobilityPrograms,
      allFlexPrograms,
      allSegments,
      availableGrhMerchant,
      availableMicromobilityMerchant,
      availableFlexMerchant,
      incompleteLaunch,
      draftState,
      allTags ?? []
    )
    setSuperPrograms(superProgramsHere)
  }, [
    availableGrhMerchant,
    availableMicromobilityMerchant,
    availableFlexMerchant,
    allGrhPrograms,
    allQtfPrograms,
    allMicromobilityPrograms,
    allFlexPrograms,
    allSegments,
    allTags,
    incompleteLaunch,
    draftState,
  ])

  if (superPrograms.length === 0) {
    return <NoPrograms />
  }

  const handleRowClick = (superProgram: any) => {
    setCurrentSuperProgram(superProgram)
    setIsSummaryDrawerOpen(true)
  }

  const onSummaryDrawerClose = () => {
    setIsSummaryDrawerOpen(false)
  }

  const onEditClick = async (superProgram: any) => {
    await dispatch(
      launchProgramsActions.applyRehydrateState({
        selectedDate: superProgram.selectedDate,
        selectedSegments: superProgram.selectedSegments,
        selectedPrograms: superProgram.selectedPrograms,
        programName: superProgram.name || '',
        currency: superProgram.currency || 'USD',
      })
    )
    await dispatch(
      programEditingActions.applyRehydrateState({
        grh: superProgram.grh,
        micromobility: superProgram.micromobility,
        flex: superProgram.flex,
        transitPass: superProgram.transitPass,
        coupon: superProgram.coupon,
        reportedUsage: superProgram.reportedUsage,
        qtf: superProgram.qtf,
      })
    )
    // Clearing any program success state
    await dispatch(programSuccessActions.newEditCycleCleanup())
    // Navigating to the edit page
    // if the program is a draft, its fromUpdate is false
    // its a launch program
    navigate(Locations.Programs.Edit, {
      state: {
        fromUpdate: superProgram.status !== 'DRAFT',
        programIds: superProgram.programIds,
        initialSelectedSegments: superProgram.selectedSegments,
        launchId: superProgram.launchId,
      },
    })
  }

  return (
    <>
      <TopButtonContainer>
        <PrimaryButton
          startIcon={<AddIcon />}
          onClick={() => navigate(Locations.Programs.Launch)}
        >
          New Program
        </PrimaryButton>
      </TopButtonContainer>
      <ProgramTable
        superPrograms={superPrograms}
        onEditClick={onEditClick}
        onRowClick={handleRowClick}
      />
      <SummaryDrawerView
        isOpen={isSummaryDrawerOpen}
        onClose={onSummaryDrawerClose}
        superProgram={currentSuperProgram}
        onEditClick={onEditClick}
      />
    </>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  OverviewView.displayName = 'ProgramsOverviewView'
}

export default React.memo(OverviewView)
